import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from '@chakra-ui/icons'
import { MdLocationOn } from 'react-icons/md'
import { useGetLocationsQuery } from '../locationSlice'
import { useLocationSearch } from '../hooks/useLocationSearch'

interface LocationFilterProps {
  selectedLocationId: string
  onChange: (locationId: string) => void
}

export const LocationFilter = ({ selectedLocationId, onChange }: LocationFilterProps) => {
  const { data: locations = [] } = useGetLocationsQuery()
  const { searchQuery, setSearchQuery, filteredLocations } = useLocationSearch(locations)

  const selectedLocation = locations.find((loc) => loc.id === selectedLocationId)

  return (
    <Menu matchWidth onClose={() => setSearchQuery('')}>
      {({ isOpen }) => (
        <>
          <MenuButton
            as={Button}
            variant="outline"
            bg="white"
            px={4}
            h="10"
            borderRadius="lg"
            borderColor="gray.200"
            fontWeight="normal"
            _hover={{ bg: 'gray.50' }}
            _active={{ bg: 'gray.100' }}
            rightIcon={
              isOpen ? (
                <ChevronUpIcon color="gray.500" boxSize={4} />
              ) : (
                <ChevronDownIcon color="gray.500" boxSize={4} />
              )
            }
          >
            <Flex align="center" gap={2}>
              <Icon as={MdLocationOn} color="gray.400" boxSize={4} />
              <Text fontSize="sm" fontWeight="medium" color="gray.700" isTruncated maxW="200px">
                {selectedLocationId && selectedLocation ? selectedLocation.name : 'All locations'}
              </Text>
            </Flex>
          </MenuButton>
          <MenuList p={0} overflow="hidden" borderRadius="lg" boxShadow="lg" zIndex={10}>
            <Box bg="white" px={3} py={2} borderBottomWidth="1px" borderColor="gray.100">
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" boxSize={3} />
                </InputLeftElement>
                <Input
                  placeholder="Search locations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  borderRadius="md"
                  bg="gray.50"
                  border="none"
                  fontSize="sm"
                  _focus={{ bg: 'white', ring: 1, ringColor: 'blue.400' }}
                />
              </InputGroup>
            </Box>
            <Box maxH="300px" overflowY="auto" py={2}>
              <MenuItem
                onClick={() => onChange('')}
                px={4}
                py={2}
                bg={!selectedLocationId ? 'blue.50' : 'transparent'}
                _hover={{ bg: !selectedLocationId ? 'blue.100' : 'gray.50' }}
              >
                <Flex align="center" gap={2}>
                  <Flex w="14px" justify="center">
                    <Box
                      w={2}
                      h={2}
                      borderRadius="full"
                      bg={!selectedLocationId ? 'blue.500' : 'gray.400'}
                    />
                  </Flex>
                  <Text
                    fontSize="sm"
                    color={!selectedLocationId ? 'blue.700' : 'gray.700'}
                    fontWeight={!selectedLocationId ? 'medium' : 'normal'}
                  >
                    All locations
                  </Text>
                </Flex>
              </MenuItem>
              {filteredLocations.map((location) => {
                const isSelected = location.id === selectedLocationId
                return (
                  <MenuItem
                    key={location.id}
                    onClick={() => onChange(location.id)}
                    px={4}
                    py={2}
                    bg={isSelected ? 'blue.50' : 'transparent'}
                    _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                  >
                    <Flex align="center" gap={2}>
                      <Icon
                        as={MdLocationOn}
                        color={isSelected ? 'blue.500' : 'red.400'}
                        boxSize={3.5}
                      />
                      <Text
                        fontSize="sm"
                        color={isSelected ? 'blue.700' : 'gray.700'}
                        fontWeight={isSelected ? 'medium' : 'normal'}
                      >
                        {location.name}
                      </Text>
                    </Flex>
                  </MenuItem>
                )
              })}
              {filteredLocations.length === 0 && (
                <Box py={3} px={4} textAlign="center">
                  <Text fontSize="sm" color="gray.500">
                    No locations found
                  </Text>
                </Box>
              )}
            </Box>
          </MenuList>
        </>
      )}
    </Menu>
  )
}
